import React from 'react';
import { setSelectedTask } from '../../../actions/tools/OrdersSearchActions';
import { shopifyUpdateOrderState } from '../../../actions/tools/ShopifyActions';
import { cancelRefund, fetchCancelInfo } from '../../../actions/tools/CommonOrderActions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  extractSelectedInboxTab,
  extractSelectedTicketFromState,
  extractSelectedTicketIdFromState,
  extractSelectedTicketLongTransactionStatus,
  isSpecialist,
} from '../../../extractFromState';
import { getSelectedOrder, getSelectedShop, selectSelectedToolDomains } from '../../../selectors/tools';
import PropTypes from 'prop-types';
import Loading from '../../LoadingComponent/LoadingComponent';
import moment from 'moment';
import { Button, FullColoredButton } from '../../common/buttonsComponents';
import * as Styled from '../common/CommonStyledComponents';
import { oneBtnPopupCreator, twoBtnPopupCreator } from '../../popups/ConfirmPopup/helper';
import cloneDeep from 'lodash/cloneDeep';
import ShopifyPolicy from './ShopifyPolicy/ShopifyPolicy';
import OrderDisabledShippedItems from '../common/OrderDisabledShippedItems';
import OrderItemsSelectItem from '../common/OrderItemsSelectItem';
import OrderSummaryBoxFromServer from '../common/OrderSummaryBoxFromServer';
import get from 'lodash/get';
import { getSimplrExpertPtrId } from '../../../api/expert';
import { loadLongTransaction } from '../../../actions';
import BigNumber from 'bignumber.js';
import { handleError } from 'services/ErrorService/ErrorService';

const DESCRIPTION_WITH_CANCELABLE =
  'The customer has paid for this order but it has not shipped, so you can issue a refund along the cancellation.';
const DESCRIPTION_ONLY_SHIPPED =
  'The customer has paid for this order but it has been shipped, so you cannot cancel and refund this order.';
const DESCRIPTION_CANCELLED = 'All cancellable items in this order were already cancelled.';
const DESCRIPTION_PARTIALLY_INVOICED_ORDER = `This is partially paid order. We do not support these kind of orders. Please escalate this ticket.`;
const DESCRIPTION_NOT_INVOICED_ORDER = `This is unpaid order. You can only fully cancel it.`;
const DESCRIPTION_INVOICE_VOIDED = `The payments have been voided. We do not support these kind of orders. Please escalate this ticket.`;

class ShopifyCancelOrder extends React.Component {
  static propTypes = {
    selectedOrder: PropTypes.object.isRequired,
    selectedTicket: PropTypes.object,
    setSelectedTask: PropTypes.func,
    selectedToolDomains: PropTypes.object,
    fetchCancelInfo: PropTypes.func,
    cancelRefund: PropTypes.func,
    isSpecialist: PropTypes.bool,
  };

  static defaultProps = {
    setSelectedTask: () => {},
    fetchCancelInfo: () => Promise.resolve(),
    cancelRefund: () => Promise.resolve(),
  };

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const {
      status,
      shippingCost,
      refundableItems,
      shippedItems,
      currencyCode,
      customData: { orderInvoiceStatus, name },
    } = await this.props.fetchCancelInfo({
      orderId: this.orderId,
    });

    if (this._isMounted === false) {
      return;
    }

    const selectedItems = {};
    for (let refundableItem of refundableItems) {
      selectedItems[refundableItem['id']] = {
        qty: refundableItem['maxSelectable'],
        active: true,
      };
    }

    this.setState({
      name: typeof name === 'undefined' ? this.orderId : name,
      refundableItems,
      shippedItems,
      selectedItems,
      status,
      shippingCost,
      currencyCode,
      orderInvoiceStatus,
      isCancelOrderItemsRequesting: false,
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  state = {
    status: '',
    shippingCost: '0.0',
    currencyCode: 'USD',
    refundableItems: [],
    shippedItems: [],
    selectedItems: {},
    reasonComment: '',
    orderInvoiceStatus: null,
    name: null,
    isCancelOrderItemsRequesting: true,
    isUpdateOrderRequesting: false,
    isPolicyAccepted: false,
    summaryBlock: {},
  };

  get orderId() {
    return String(this.props.selectedOrder.id);
  }

  get shopifyConfig() {
    const configs = this.props.selectedToolDomains['shopifyConfigs'] || {};

    return configs[this.props.selectedShop] || {};
  }

  get isInvoiceVoided() {
    return this.state.orderInvoiceStatus === 'voided';
  }

  get isNotInvoicedOrder() {
    return this.state.orderInvoiceStatus === 'not_invoiced';
  }

  get isPartiallyInvoicedOrder() {
    return this.state.orderInvoiceStatus === 'partially_invoiced';
  }

  get isOrderStateCancellable() {
    return !['cancelled'].includes(this.state.status) && !this.isPartiallyInvoicedOrder && !this.isInvoiceVoided;
  }

  get hasRefundableItems() {
    return this.isOrderStateCancellable && !!this.state.refundableItems.length;
  }

  get hasShippedItems() {
    return this.isOrderStateCancellable && !!this.state.shippedItems.length;
  }

  get placeholderText() {
    const { status } = this.state;

    if (status === 'cancelled') {
      return DESCRIPTION_CANCELLED;
    }

    if (this.isInvoiceVoided) {
      return DESCRIPTION_INVOICE_VOIDED;
    }

    if (this.isNotInvoicedOrder) {
      return DESCRIPTION_NOT_INVOICED_ORDER;
    }

    if (this.isPartiallyInvoicedOrder) {
      return DESCRIPTION_PARTIALLY_INVOICED_ORDER;
    }

    if (this.hasRefundableItems) {
      return DESCRIPTION_WITH_CANCELABLE;
    }

    if (this.hasShippedItems) {
      return DESCRIPTION_ONLY_SHIPPED;
    }

    return DESCRIPTION_CANCELLED;
  }

  get refundConfig() {
    return this.shopifyConfig.refundConfig || {};
  }

  get refundPolicy() {
    return this.refundConfig.refundPolicy || {};
  }

  get refundLimitPrice() {
    const maxPriceAllowed = this.refundPolicy['maxPriceAllowed'];

    return typeof maxPriceAllowed === 'undefined' ? 0 : parseInt(maxPriceAllowed);
  }

  handleItemQtyChange = (itemId, newQty) => {
    const selectedItems = cloneDeep(this.state.selectedItems);
    selectedItems[itemId].qty = newQty;
    if (BigNumber(newQty).isEqualTo(BigNumber(0))) {
      selectedItems[itemId].active = !selectedItems[itemId].active;
    }
    this.setState({ selectedItems });
  };

  handleSelectItem = (itemId) => {
    const selectedItems = cloneDeep(this.state.selectedItems);
    selectedItems[itemId].active = !selectedItems[itemId].active;
    this.setState({ selectedItems });
  };

  get isAllItemsSelected() {
    return this.state.refundableItems.every((item) => {
      const selectedItem = this.state.selectedItems[item['id']];

      return item['maxSelectable'] === selectedItem.qty && selectedItem.active;
    });
  }

  handleSelectAll = () => {
    const selectedItems = cloneDeep(this.state.selectedItems);
    const isAllItemsSelected = this.isAllItemsSelected;
    this.state.refundableItems.forEach((item) => {
      selectedItems[item['id']].active = !isAllItemsSelected;

      if (!this.isAllItemsSelected) {
        selectedItems[item['id']].qty = item['maxSelectable'];
      }
    });

    this.setState({ selectedItems });
  };

  get refundLimitDays() {
    const refundPolicy = this.refundConfig.refundPolicy || {};

    return typeof refundPolicy.days === 'undefined' ? null : refundPolicy.days;
  }

  get isSpecialist() {
    return this.props.isSpecialist;
  }

  get createdDate() {
    return (this.props.selectedOrder || {}).created_at || '';
  }

  get totalRefund() {
    return get(this, 'state.summaryBlock.totals.total', 0);
  }

  get selectedQty() {
    return this.state.refundableItems.reduce((accumulator, item) => {
      const selectedItem = this.state.selectedItems[item['id']];
      const qty = selectedItem.active ? selectedItem.qty : 0;

      return accumulator + qty;
    }, 0);
  }

  isRefundLimitExceeded() {
    return this.refundLimitPrice !== 0 ? this.refundLimitPrice <= this.totalRefund : false;
  }

  displayPopupErrorMessage(message = '') {
    oneBtnPopupCreator({
      content: {
        main: message,
        iconPath: '/images/error-esclamation.svg',
      },
      btnConfig: 'CLOSE',
    });
  }

  handleRefundBtnClick = () => {
    if (this.selectedQty === 0) {
      return this.displayPopupErrorMessage('Please add at least one item');
    }
    if (!this.state.reasonComment.trim()) {
      return this.displayPopupErrorMessage('Please add reason for cancel');
    }

    if (this.isRefundLimitExceeded()) {
      return this.displayPopupErrorMessage(`Partner limits for refund $${this.refundLimitPrice}`);
    }

    twoBtnPopupCreator({
      content: {
        main: 'Are you sure you want to cancel the order?',
        iconPath: '/images/green-alert-bell.svg',
      },
      btnConfig: {
        dismiss: 'DISMISS',
        confirm: 'CANCEL ORDER',
      },
      onConfirm: this.confirmationHandler,
    });
  };

  handlerTextInputChange = ({ target: { name, value } }) => {
    this.setState({ [name]: value });
  };

  onChangeTotals = (totals) => {
    this.setState({ summaryBlock: { ...this.state.summaryBlock, ...totals } });
  };

  get refundItems() {
    return this.state.refundableItems
      .map((item) => {
        const selectedItem = this.state.selectedItems[item['id']];
        const qty = selectedItem.active ? selectedItem.qty : 0;

        return {
          id: String(item['id']),
          quantity: qty,
        };
      })
      .filter((refundItem) => {
        return refundItem.quantity > 0;
      });
  }

  requesting(requestName, status) {
    this.setState({ [requestName]: status });
  }

  confirmationHandler = async () => {
    this.requesting('isCancelRequesting', true);

    const userId = getSimplrExpertPtrId();

    const payload = {
      orderId: this.orderId,
      refundItems: this.refundItems,
      comment: this.state.reasonComment,
      conversationId: this.props.selectedTicket.ticketId,
      addNotes: true,
      refundAmount: `${this.totalRefund.toFixed(2)} ${this.state.currencyCode}`,
      timeOut: 70000,
      userId,
    };

    this.props.loadLongTransaction();
    try {
      await this.props.cancelRefund(payload);
    } catch (error) {
      handleError({ error });
      this.requesting('isCancelRequesting', false);

      return null;
    }

    await this.props.setSelectedTask(null);
    this.updateSelectedOrder();
    this.requesting('isCancelRequesting', false);

    return null;
  };

  async updateSelectedOrder() {
    this.requesting('isUpdateOrderRequesting', true);

    try {
      await this.props.shopifyUpdateOrderState(this.orderId, this.props.selectedShop);
    } catch (error) {
      handleError({ error });
    }

    this.requesting('isUpdateOrderRequesting', false);
  }

  handleBackBtnClick = () => {
    this.props.setSelectedTask(null);
  };

  isRefundDateLimitExceeded() {
    const createdDateMoment = moment(this.createdDate).format('MM/DD/YYYY');
    const actualDate = moment(new Date()).format('MM/DD/YYYY');

    return !(moment(actualDate).diff(moment(createdDateMoment), 'days') <= this.refundLimitDays);
  }

  renderComponent() {
    const refundableItemsJSX = this.state.refundableItems.map((item) => {
      const itemId = item['id'];
      const selectedItem = this.state.selectedItems[itemId];

      return (
        <OrderItemsSelectItem
          id={itemId}
          active={selectedItem.active}
          name={`(${item['maxSelectable']}) ${item['name']}`}
          max={item['maxSelectable']}
          qty={selectedItem.qty}
          handleSelectItem={this.handleSelectItem}
          handleItemQtyChange={this.handleItemQtyChange}
          disableControls={this.isNotInvoicedOrder}
        />
      );
    });

    const shippedItemsJSX = this.state.shippedItems.map((item) => (
      <OrderDisabledShippedItems
        id={item['id']}
        trackingNumber={item['trackingNumber']}
        name={`(${item['amountShipped']}) ${item['name']}`}
      />
    ));

    return (
      <Styled.OrderTaskWrapper>
        <Styled.OrderTaskTitle>Cancel Order</Styled.OrderTaskTitle>
        <Styled.OrderTaskDescription>{this.placeholderText}</Styled.OrderTaskDescription>

        {this.hasRefundableItems && (
          <Styled.CancelStep>
            <Styled.StepTitle>
              <Styled.Subtext>Step 1 - </Styled.Subtext>Following items can be cancelled.
              {!this.isNotInvoicedOrder && ` Please select product(s) and change quantity if different.`}
            </Styled.StepTitle>

            {!this.isNotInvoicedOrder && (
              <Styled.ItemRow>
                <Styled.Checkbox
                  type="checkbox"
                  checked={this.isAllItemsSelected}
                  onChange={() => this.handleSelectAll()}
                />
                <Styled.RowContent>
                  <Styled.Subtext>Select all</Styled.Subtext>
                </Styled.RowContent>
              </Styled.ItemRow>
            )}

            <Styled.Divider />

            {refundableItemsJSX}

            <Styled.Divider />
          </Styled.CancelStep>
        )}

        {this.hasShippedItems && (
          <Styled.CancelStep>
            <Styled.StepTitle>Following items have been shipped and cannot be cancelled.</Styled.StepTitle>

            {shippedItemsJSX}

            <Styled.Divider />
          </Styled.CancelStep>
        )}

        {this.hasRefundableItems && (
          <Styled.CancelStep>
            <Styled.StepTitle>
              <Styled.Subtext>Step 2 - </Styled.Subtext>Select reason for cancelling <Styled.Required />
            </Styled.StepTitle>

            <Styled.RowContent>
              <Styled.RefundCommentTextarea
                name="reasonComment"
                value={this.state.reasonComment}
                onChange={this.handlerTextInputChange}
              />

              <Styled.FlexPlaceholder />

              <OrderSummaryBoxFromServer
                onChange={(totals) => this.onChangeTotals(totals)}
                currencyCode={this.state.currencyCode}
                refundItems={this.refundItems}
              />
            </Styled.RowContent>
          </Styled.CancelStep>
        )}

        <Styled.OrderTaskControls>
          <Button onClick={this.handleBackBtnClick}>BACK</Button>
          {this.hasRefundableItems && (
            <FullColoredButton
              active={this.state.summaryBlock && !this.state.summaryBlock.request}
              onClick={this.handleRefundBtnClick}
              disabled={this.state.summaryBlock && this.state.summaryBlock.request}
            >
              CANCEL ORDER
            </FullColoredButton>
          )}
        </Styled.OrderTaskControls>
      </Styled.OrderTaskWrapper>
    );
  }

  render() {
    if (this.state.isCancelRequesting || this.state.isUpdateOrderRequesting) {
      return <Loading label="Cancel order..." />;
    }

    if (this.state.isCancelOrderItemsRequesting) {
      return <Loading label="Loading cancel information..." />;
    }

    return (
      <ShopifyPolicy
        createdDate={this.createdDate}
        showCancel={true}
        showRefund={false}
        config={this.shopifyConfig}
        isSpecialist={this.isSpecialist}
      >
        {this.renderComponent()}
      </ShopifyPolicy>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedLongTransactionStatus: extractSelectedTicketLongTransactionStatus(
    state,
    extractSelectedTicketIdFromState(state)
  ),
  selectedOrder: getSelectedOrder(state),
  selectedTicket: extractSelectedTicketFromState(state),
  selectedInboxTab: extractSelectedInboxTab(state),
  selectedShop: getSelectedShop(state),
  selectedToolDomains: selectSelectedToolDomains(state),
  isSpecialist: isSpecialist(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setSelectedTask,
      fetchCancelInfo,
      cancelRefund,
      shopifyUpdateOrderState,
      loadLongTransaction,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ShopifyCancelOrder);
